import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';

export type HashLinkProps = {
  to: string;
  smooth?: boolean;
  scroll?: (el: HTMLElement) => void;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  style?: React.CSSProperties;
  children: React.ReactNode;
};

const defaultScroll = (el: HTMLElement, smooth?: boolean) => {
  el.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
};

const normalizeTo = (to: string) => {
  const withSlash = to.startsWith('/') ? to : `/${to}`;
  const [pathname, hashId] = withSlash.split('#');
  return {
    linkTo: hashId ? `${pathname}#${hashId}` : pathname,
    pathname,
    hashId,
  };
};

/** RR v7 replacement for `react-router-hash-link`. */
export const HashLink: React.FC<HashLinkProps> = ({
  to,
  smooth,
  scroll,
  onClick,
  style,
  children,
}) => {
  const location = useLocation();
  const { linkTo, pathname, hashId } = normalizeTo(to);

  const scrollToHash = React.useCallback(() => {
    if (!hashId) return false;

    const el = document.getElementById(hashId);
    if (!el) return false;

    if (scroll) {
      scroll(el);
    } else {
      defaultScroll(el, smooth);
    }
    return true;
  }, [hashId, scroll, smooth]);

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (!hashId) return;

    if (location.pathname === pathname) {
      event.preventDefault();
      scrollToHash();
      window.history.replaceState(null, '', `${pathname}#${hashId}`);
      return;
    }

    let attempts = 0;
    const tryScroll = () => {
      if (scrollToHash() || attempts >= 100) return;
      attempts += 1;
      window.setTimeout(tryScroll, 50);
    };
    window.setTimeout(tryScroll, 0);
  };

  return (
    <Link to={linkTo} style={style} onClick={handleClick}>
      {children}
    </Link>
  );
};
